import React from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTheme } from "react-native-paper";

import type { ExtendedTheme } from "../theme/SynapseTheme";
import {
  GAME_MODE_LABELS,
  gameModeOf,
  type GameReport,
} from "../utils/gameReportUtils";

type HeroTone = "perfect" | "solved" | "gaveUp";

const samePath = (a: string[] = [], b: string[] = []) =>
  a.length === b.length && a.every((w, i) => w === b[i]);

/**
 * Outcome headline for a finished game. Pure, so it is unit-tested. A win in
 * par moves (the fewest possible) is "Gold path" when the route is the gold
 * path itself, else "Fewest moves".
 */
export const reportHeadline = (
  report: Pick<
    GameReport,
    "status" | "totalMoves" | "optimalPath" | "suggestedPath" | "playerPath"
  >,
): { text: string; tone: HeroTone } => {
  const par = Math.max(0, (report.optimalPath?.length ?? 1) - 1);
  if (report.status === "won") {
    if (report.totalMoves <= par)
      return samePath(report.playerPath, report.optimalPath)
        ? { text: "Gold path", tone: "perfect" }
        : { text: "Fewest moves", tone: "perfect" };
    const n = report.totalMoves;
    return {
      text: `Solved in ${n} ${n === 1 ? "move" : "moves"}`,
      tone: "solved",
    };
  }
  const away = Math.max(0, (report.suggestedPath?.length ?? 1) - 1);
  return {
    text:
      away > 0
        ? `Gave up, ${away} ${away === 1 ? "step" : "steps"} away`
        : "Gave up",
    tone: "gaveUp",
  };
};

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** 'Daily · Oct 5' / 'Tutorial' / 'Challenge' / 'Random game'. */
export const reportModeLine = (
  report: Pick<
    GameReport,
    "isTutorial" | "isDailyChallenge" | "isChallenge" | "dailyChallengeId"
  >,
): string => {
  const mode = gameModeOf(report);
  if (mode === "random") return "Random game";
  const label = GAME_MODE_LABELS[mode] ?? "";
  if (mode === "daily") {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(report.dailyChallengeId ?? "");
    if (m) return `${label} · ${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}`;
  }
  return label;
};

/**
 * The outcome over the report graph: the headline and the mode line, nothing
 * more. The numbers (ReportStats) and her result sit in the report's Compare
 * paths section, under the rows they describe (owner, 2026-10-21: v1 style).
 */
export const ReportHero: React.FC<{ report: GameReport }> = ({ report }) => {
  const { colors, customColors } = useTheme() as ExtendedTheme;
  const { text, tone } = reportHeadline(report);
  const color =
    tone === "perfect"
      ? customColors.globalOptimalNode
      : tone === "solved"
        ? customColors.startNode
        : customColors.endNode;
  return (
    <View style={styles.hero} testID="report-hero">
      <Text style={[styles.heroText, { color }]}>{text}</Text>
      <Text style={[styles.heroSub, { color: colors.onSurfaceVariant }]}>
        {reportModeLine(report)} · {report.startWord} → {report.targetWord}
      </Text>
    </View>
  );
};

const StatLine: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => {
  const { colors } = useTheme() as ExtendedTheme;
  return (
    <View style={styles.statLine}>
      <Text style={[styles.statLabel, { color: colors.onSurfaceVariant }]}>
        {label}
      </Text>
      <Text style={[styles.statValue, { color: colors.onSurface }]}>
        {value}
      </Text>
    </View>
  );
};

/**
 * The game's numbers as plain lines, in the report's Compare paths section
 * under the path rows (owner, 2026-10-21: v1 style, together with the path
 * expansions). Accuracy, moves against par, distance against gold, and the
 * backtrack count only when there were any.
 */
export const ReportStats: React.FC<{ report: GameReport }> = ({ report }) => {
  const par = Math.max(0, report.optimalPath.length - 1);
  const backtracks =
    report.backtrackEvents?.length || report.backtrackCount || 0;
  return (
    <View style={styles.stats} testID="report-stats">
      <StatLine label="Accuracy" value={`${report.moveAccuracy.toFixed(0)}%`} />
      <StatLine label="Moves" value={`${report.totalMoves} · par ${par}`} />
      <StatLine
        label="Distance"
        value={`${report.playerSemanticDistance.toFixed(2)} · gold ${report.optimalSemanticDistance.toFixed(2)}`}
      />
      {backtracks > 0 && (
        <StatLine label="Backtracks" value={`${backtracks}`} />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  hero: {
    paddingHorizontal: 20,
    paddingTop: 14,
    paddingBottom: 6,
  },
  heroText: {
    fontSize: 22,
    lineHeight: 28,
    fontWeight: "700",
  },
  heroSub: {
    fontSize: 13,
    marginTop: 2,
  },
  stats: {
    marginTop: 4,
    gap: 2,
  },
  statLine: {
    flexDirection: "row",
    alignItems: "baseline",
    gap: 12,
  },
  statLabel: {
    width: 84,
    fontSize: 13,
    fontWeight: "600",
  },
  statValue: {
    fontSize: 13,
    fontVariant: ["tabular-nums"],
  },
});
